/**
 * Phase 6 — invite-token access helpers.
 *
 * The app uses a single shared bearer token stored in `localStorage` as a
 * lightweight "private link" gate. This module is the single source of
 * truth for reading / writing that token, capturing it from the URL on
 * first visit, and reaching the public `/api/access/*` endpoints.
 *
 * Everything else just uses `apiClient` which already has interceptors
 * wired to inject the token automatically.
 */
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "access_token";

/** Read the current token from localStorage. Empty string when absent. */
export function getStoredToken() {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || "";
  } catch {
    return "";
  }
}

/** Persist a token. */
export function setStoredToken(token) {
  try {
    if (token) window.localStorage.setItem(STORAGE_KEY, token);
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // localStorage unavailable (private/incognito mode, storage quota
    // exhausted, sandboxed iframe, etc.). Log but don't surface to UI
    // — the in-memory axios interceptor still has the token for this
    // session, so the app keeps working until reload.
    console.warn("accessApi.setStoredToken: localStorage write failed", e);
  }
}

/** Clear the stored token. */
export function clearStoredToken() {
  setStoredToken("");
}

/**
 * If the current URL contains `?token=…`, save the value to localStorage
 * and clean it off the URL via `history.replaceState`. Returns the
 * captured token (or "" if none was in the URL).
 *
 * Idempotent — calling it twice does nothing the second time.
 */
export function captureTokenFromUrl() {
  if (typeof window === "undefined") return "";
  const url = new URL(window.location.href);
  const tok = url.searchParams.get("token");
  if (!tok) return "";
  url.searchParams.delete("token");
  setStoredToken(tok);
  // Preserve hash + remaining params; just drop `token`.
  const cleaned = url.pathname + (url.search ? url.search : "") + url.hash;
  window.history.replaceState({}, "", cleaned);
  return tok;
}

/* ─────────────────────  Public access endpoints  ───────────────────── */
// These three are reachable WITHOUT a token (the backend allowlist exempts
// /api/access/*). We use a raw axios instance here — NOT the shared
// `apiClient` — so the request never gets the Authorization interceptor
// applied. That's important for the bootstrap flow: we don't want the
// validate request to inherit a stale/bad token from localStorage.
const raw = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
});

export const checkHasToken = () =>
  raw.get("/access/token").then((r) => r.data); // → { has_token: boolean }

export const validateToken = (token) =>
  raw
    .post("/access/validate", { token })
    .then((r) => r.data); // → { valid: boolean }

/** Builds the absolute invite URL for the supplied token. */
export function buildInviteUrl(token) {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/?token=${token}`;
}

/**
 * Mask a token for display, e.g. `Abc123…wXyZ`. Used by the settings
 * panel so the full token isn't rendered to the screen by default.
 */
export function maskToken(token) {
  if (!token) return "";
  if (token.length <= 12) return "…";
  return `${token.slice(0, 6)}…${token.slice(-4)}`;
}
